import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getGameMeta } from '@shared/data/gamesMeta';
import {
  getScoringRowsForSelection,
  scoringRowsToBlocks,
} from '@shared/data/scoringForSelection';
import {
  ALIEN_INVASION_EXPANSION_ID,
  alienInvasionExpansionScenarios,
} from '@shared/data/on-mars/alien-invasion/expansion';
import { getAlienInvasionSection } from '@shared/data/on-mars/alien-invasion';
import { MARINE_WORLDS_EXPANSION_ID } from '@shared/data/ark-nova/marine-worlds/expansion';
import {
  MARINE_WORLDS_CHAPTER_TITLE,
  arkNovaMarineChapters,
} from '@shared/data/ark-nova/content/marine-worlds';
import { perfectRulebooks } from '@shared/data/perfect-quality/generatedRulebooks';
import { buildFiguresTable } from '@shared/games/carcassonne/data/figure-rules';
import type { CarcassonneModuleId } from '@shared/games/carcassonne/types';
import { vinhosEditions } from '@shared/games/vinhos-deluxe/metadata';
import { getPwaGameBundle } from '../content/gameBundle';
import { ContentBlocks } from '../components/ContentBlocks';
import { ExpansionToggleList } from '../components/ExpansionToggleList';
import { useGamePrefs } from '../state/GamePrefsProvider';
import type { ContentBlock } from '@shared/types';

type SectionKey =
  | 'first-game'
  | 'faq'
  | 'quick-reference'
  | 'glossary'
  | 'scoring'
  | 'mistakes'
  | 'expansions'
  | 'search'
  | 'qr'
  | 'alien-invasion'
  | 'figures-reference'
  | 'marine-worlds'
  | 'perfect-quality'
  | 'ark-nova-special';

const titles: Record<SectionKey, string> = {
  'first-game': 'Pierwsza gra',
  faq: 'FAQ',
  'quick-reference': 'Szybka ściąga',
  glossary: 'Słownik',
  scoring: 'Punktacja',
  mistakes: 'Częste błędy',
  expansions: 'Dodatki i warianty',
  search: 'Szukaj w grze',
  qr: 'Kod / deep-link QR',
  'alien-invasion': 'Alien Invasion',
  'figures-reference': 'Pionki i figurki',
  'marine-worlds': 'Zwierzęta Wodne',
  'perfect-quality': 'Przewodnik i przykłady',
  'ark-nova-special': 'Ark Nova — specjalne',
};

const MARINE_HUB_TOPICS = [
  'Co dodaje rozszerzenie',
  'Zmiany przygotowania gry',
  'Zwierzęta morskie',
  'Akwaria',
  'Rafa',
  'Alternatywne karty akcji',
  'Nowe karty, projekty i żetony premii',
  'Przerwa i koniec gry z dodatkiem',
  'Checklista zagrania zwierzęcia morskiego',
];

export function SectionScreen() {
  const { gameId = '', section = '' } = useParams();
  const key = section as SectionKey;
  const game = getGameMeta(gameId);
  const bundle = useMemo(() => getPwaGameBundle(gameId), [gameId]);
  const [query, setQuery] = useState('');
  const {
    getPrefs,
    getOptions,
    isSelected,
    setExpansionEnabled,
    setActiveVariant,
    setChecklistChecked,
    checklistMap,
    patchPrefs,
  } = useGamePrefs();

  const prefs = getPrefs(gameId);
  const options = getOptions(gameId);

  if (key === 'first-game') {
    return <Navigate to={`/game/${gameId}/first-game`} replace />;
  }

  if (!game || !(key in titles)) {
    return (
      <div className="empty">
        <h1>Nie znaleziono sekcji</h1>
        <Link to={`/game/${gameId}`}>Wróć do gry</Link>
      </div>
    );
  }

  let blocks: ContentBlock[] = [];
  if (key === 'faq') blocks = bundle.faqBlocks;
  if (key === 'quick-reference') blocks = bundle.quickReferenceBlocks;
  if (key === 'glossary') blocks = bundle.glossaryBlocks;
  if (key === 'scoring') {
    const rows = getScoringRowsForSelection(gameId, prefs);
    blocks =
      rows.length > 0
        ? scoringRowsToBlocks(rows)
        : bundle.scoringBlocks.length > 0
          ? bundle.scoringBlocks
          : [
              {
                type: 'paragraph',
                text: 'Brak treści punktacji dla wybranej gry.',
              },
            ];
  }
  if (key === 'mistakes') blocks = bundle.mistakesBlocks;

  if (key === 'alien-invasion') {
    const scenarioId =
      prefs.selectedScenarioIds[ALIEN_INVASION_EXPANSION_ID] ?? null;
    blocks = [
      {
        type: 'paragraph',
        text: 'Wybierz scenariusz Alien Invasion. Treść jest niezależna od bazy ON MARS.',
      },
      ...(scenarioId
        ? [
            {
              type: 'tip' as const,
              text: `Aktywny scenariusz: ${scenarioId}`,
            },
          ]
        : [
            {
              type: 'warning' as const,
              text: 'Nie wybrano scenariusza — wybierz jeden poniżej.',
            },
          ]),
    ];
  }

  if (key === 'perfect-quality') {
    const book = perfectRulebooks[gameId];
    blocks = book?.examples?.length
      ? book.examples
      : book?.quickReference?.length
        ? book.quickReference
        : [
            {
              type: 'paragraph',
              text: 'Brak dodatkowych przykładów perfect-quality dla tej gry.',
            },
          ];
  }

  if (key === 'figures-reference' && gameId === 'carcassonne') {
    const moduleIds = (
      prefs.selectedModuleIds.length > 0
        ? prefs.selectedModuleIds
        : ['base']
    ) as CarcassonneModuleId[];
    const rows = buildFiguresTable(moduleIds);
    blocks = [
      {
        type: 'paragraph',
        text: `Jedna tabela dla aktywnej konfiguracji (${rows.length} wpisów). Wiersze niedostępnych dodatków są ukryte.`,
      },
      ...rows.flatMap((row) => [
        {
          type: 'heading' as const,
          text: `${row.name} (${row.moduleShortName})`,
          level: 3 as const,
        },
        {
          type: 'step-list' as const,
          steps: [
            `Gdzie: ${row.wherePlaced}`,
            `Kiedy: ${row.whenPlaced}`,
            `Siła większości: ${row.majorityStrength}`,
            `Punktacja: ${row.scoring}`,
            `Powrót: ${row.whenReturns}`,
            `Ograniczenia: ${row.restrictions}`,
            row.isPlayerMeeple
              ? 'Meeple gracza — liczy się do większości.'
              : 'Nie jest meeplem gracza na obszarach mapy.',
          ],
        },
      ]),
    ];
  }

  if (key === 'marine-worlds' || key === 'ark-nova-special') {
    const enabled = prefs.selectedExpansionIds.includes(
      MARINE_WORLDS_EXPANSION_ID
    );
    const chapterTitle =
      arkNovaMarineChapters[0]?.title ?? MARINE_WORLDS_CHAPTER_TITLE;
    const chapterIndex = bundle.manual.chapters.findIndex(
      (chapter) => chapter.title === chapterTitle
    );
    blocks = [
      {
        type: 'paragraph',
        text: enabled
          ? 'Hub dodatku Zwierzęta Wodne — te same tematy co w Expo.'
          : 'Włącz dodatek Zwierzęta Wodne w Dodatki / warianty, aby odblokować treść.',
      },
      {
        type: 'tip',
        text: `Rozdział: ${chapterTitle}${chapterIndex >= 0 ? ` (indeks ${chapterIndex})` : ''}`,
      },
      { type: 'heading', text: 'Tematy', level: 2 },
      { type: 'step-list', steps: MARINE_HUB_TOPICS },
    ];
  }

  const deepLink =
    typeof window !== 'undefined'
      ? `${window.location.origin}/game/${gameId}`
      : `/game/${gameId}`;

  const searchHits =
    key === 'search'
      ? bundle.searchDocuments.filter((doc) => {
          const q = query.trim().toLocaleLowerCase('pl');
          if (!q) return true;
          return `${doc.title} ${doc.body}`.toLocaleLowerCase('pl').includes(q);
        })
      : [];

  return (
    <section className="panel" data-testid={`section-${key}`}>
      <p className="eyebrow">{game.title}</p>
      <h1>{titles[key]}</h1>

      {key === 'expansions' ? (
        <div style={{ marginTop: 18 }} data-testid="expansions-panel">
          <p className="subtitle">
            Włącz dodatki / moduły — punktacja i treść aktualizują się od razu.
            Stan zapisuje się per gra.
          </p>

          {gameId === 'vinhos-deluxe' ? (
            <div className="variant-picker" data-testid="variant-picker">
              <h2>Wariant gry</h2>
              <p className="meta">
                Każdy wariant ma niezależny postęp czytania i pierwszej gry.
              </p>
              <div className="guided-choices">
                {vinhosEditions
                  .filter((edition) => edition.activatable)
                  .map((edition) => (
                    <button
                      key={edition.id}
                      type="button"
                      className={`primary-btn${prefs.activeVariantId === edition.id ? ' selected-variant' : ''}`}
                      data-testid={`variant-${edition.id}`}
                      aria-pressed={prefs.activeVariantId === edition.id}
                      onClick={() => setActiveVariant(gameId, edition.id)}
                    >
                      {edition.name}
                      <span className="meta" style={{ display: 'block' }}>
                        {edition.description}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          ) : null}

          <ExpansionToggleList
            options={options}
            isSelected={(option) => isSelected(gameId, option)}
            onToggle={(option, enabled) =>
              setExpansionEnabled(
                gameId,
                option.id,
                enabled,
                Boolean(option.storeAsModule)
              )
            }
          />

          {gameId === 'on-mars' &&
          prefs.selectedExpansionIds.includes(ALIEN_INVASION_EXPANSION_ID) ? (
            <div style={{ marginTop: 20 }} data-testid="scenario-picker">
              <h2>Scenariusz Alien Invasion</h2>
              <div className="guided-choices">
                {alienInvasionExpansionScenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    type="button"
                    className={`primary-btn${
                      prefs.selectedScenarioIds[ALIEN_INVASION_EXPANSION_ID] ===
                      scenario.id
                        ? ' selected-variant'
                        : ''
                    }`}
                    data-testid={`scenario-${scenario.id}`}
                    onClick={() =>
                      patchPrefs(gameId, {
                        selectedScenarioIds: {
                          ...prefs.selectedScenarioIds,
                          [ALIEN_INVASION_EXPANSION_ID]: scenario.id,
                        },
                      })
                    }
                  >
                    {scenario.title}
                    <span className="meta" style={{ display: 'block' }}>
                      {scenario.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {key === 'search' ? (
        <>
          <div className="search-box" style={{ marginTop: 18 }}>
            <span aria-hidden>⌕</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Szukaj w instrukcji, FAQ, punktacji…"
              aria-label="Szukaj w grze"
              data-testid="search-input"
            />
          </div>
          <div className="content-stack">
            {searchHits.slice(0, 40).map((hit) => (
              <Link key={hit.id} to={hit.href} className="hub-tile">
                {hit.title}
                <span>{hit.body.slice(0, 140)}…</span>
              </Link>
            ))}
            {searchHits.length === 0 ? (
              <p className="meta">Brak wyników.</p>
            ) : null}
          </div>
        </>
      ) : null}

      {key === 'qr' ? (
        <div>
          <p className="subtitle">
            Otwórz ten link na innym urządzeniu albo wygeneruj QR zewnętrznie.
          </p>
          <div className="qr-box">{deepLink}</div>
          <p className="meta" style={{ marginTop: 12 }}>
            Deep-linki rozdziałów: /game/{gameId}/manual/0
          </p>
        </div>
      ) : null}

      {key === 'alien-invasion' ? (
        <div style={{ marginTop: 18 }} data-testid="alien-invasion-panel">
          <ContentBlocks blocks={blocks} />
          <div className="guided-choices" style={{ marginTop: 16 }}>
            {alienInvasionExpansionScenarios.map((scenario) => (
              <button
                key={scenario.id}
                type="button"
                className="primary-btn"
                data-testid={`alien-open-${scenario.id}`}
                onClick={() => {
                  const firstSection = scenario.sectionIds[0];
                  patchPrefs(gameId, {
                    selectedScenarioIds: {
                      ...prefs.selectedScenarioIds,
                      [ALIEN_INVASION_EXPANSION_ID]: scenario.id,
                    },
                    lastAlienSectionId: firstSection ?? null,
                  });
                }}
              >
                Otwórz: {scenario.title}
              </button>
            ))}
          </div>
          {prefs.lastAlienSectionId ? (
            <AlienSectionPreview sectionId={prefs.lastAlienSectionId} />
          ) : null}
        </div>
      ) : null}

      {key === 'marine-worlds' || key === 'ark-nova-special' ? (
        <div style={{ marginTop: 18 }} data-testid={`${key}-panel`}>
          <ContentBlocks blocks={blocks} />
          {(() => {
            const chapterTitle =
              arkNovaMarineChapters[0]?.title ?? MARINE_WORLDS_CHAPTER_TITLE;
            const chapterIndex = bundle.manual.chapters.findIndex(
              (chapter) => chapter.title === chapterTitle
            );
            return chapterIndex >= 0 ? (
              <Link
                className="primary-btn"
                style={{ display: 'inline-block', marginTop: 16 }}
                to={`/game/${gameId}/manual/${chapterIndex}`}
                data-testid="marine-open-chapter"
              >
                Otwórz rozdział Zwierzęta Wodne
              </Link>
            ) : (
              <Link
                className="primary-btn"
                style={{ display: 'inline-block', marginTop: 16 }}
                to={`/game/${gameId}/expansions`}
                data-testid="marine-enable-addon"
              >
                Włącz dodatek w Dodatki / warianty
              </Link>
            );
          })()}
          <div style={{ marginTop: 12 }}>
            <Link className="ghost-btn" to={`/game/${gameId}/faq`}>
              FAQ Ark Nova
            </Link>
          </div>
        </div>
      ) : null}

      {key === 'figures-reference' || key === 'perfect-quality' ? (
        <div
          style={{ marginTop: 18 }}
          data-testid={`${key}-panel`}
        >
          <ContentBlocks blocks={blocks} />
        </div>
      ) : null}

      {key !== 'search' &&
      key !== 'qr' &&
      key !== 'expansions' &&
      key !== 'alien-invasion' &&
      key !== 'marine-worlds' &&
      key !== 'ark-nova-special' &&
      key !== 'figures-reference' &&
      key !== 'perfect-quality' ? (
        <div
          style={{ marginTop: 18 }}
          data-testid={key === 'scoring' ? 'scoring-panel' : undefined}
        >
          {blocks.length > 0 ? (
            <ContentBlocks
              blocks={blocks}
              checklistId={`${gameId}:${key}`}
              checklistState={checklistMap(gameId)}
              onToggleChecklist={(itemKey, checked) =>
                setChecklistChecked(gameId, itemKey, checked)
              }
            />
          ) : (
            <p className="meta">Brak treści w tej sekcji dla wybranej gry.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

function AlienSectionPreview({ sectionId }: { sectionId: string }) {
  const section = getAlienInvasionSection(sectionId);
  if (!section) {
    return <p className="meta">Brak sekcji: {sectionId}</p>;
  }
  const content = (section.content ?? []) as ContentBlock[];
  return (
    <div className="panel" style={{ marginTop: 16 }} data-testid="alien-section">
      <h2>{section.title}</h2>
      {content.length > 0 ? <ContentBlocks blocks={content} /> : null}
    </div>
  );
}
